/**
 * Agent Actions Service
 *
 * Type-safe agent actions management service using @monorepo/shared schemas
 */

import {
  type AgentActionQuery,
  type AgentActionResponse,
  AgentActionResponseSchema,
  type CreateAgentAction,
  CreateAgentActionSchema,
  type PaginatedAgentActionsResponse,
  PaginatedAgentActionsResponseSchema,
  type UpdateAgentAction,
  UpdateAgentActionSchema,
} from '@monorepo/shared/schemas/agents'
import { del, get, post, put } from '@/lib/api-client'

export const agentActionsService = {
  /**
   * List actions for an agent
   */
  async getActions(
    agentId: string,
    query?: AgentActionQuery
  ): Promise<PaginatedAgentActionsResponse> {
    const queryParams = new URLSearchParams()

    if (query?.action_type) queryParams.append('action_type', query.action_type)
    if (query?.search) queryParams.append('search', query.search)
    if (query?.page) queryParams.append('page', query.page.toString())
    if (query?.limit) queryParams.append('limit', query.limit.toString())

    const url = `/agents/${agentId}/actions${queryParams.toString() ? `?${queryParams.toString()}` : ''}`

    return get(url, PaginatedAgentActionsResponseSchema)
  },

  /**
   * Get action by ID
   */
  async getAction(
    agentId: string,
    actionId: string
  ): Promise<AgentActionResponse> {
    return get(
      `/agents/${agentId}/actions/${actionId}`,
      AgentActionResponseSchema
    )
  },

  /**
   * Create new action for agent
   */
  async createAction(
    agentId: string,
    data: CreateAgentAction
  ): Promise<AgentActionResponse> {
    const validated = CreateAgentActionSchema.parse(data)

    return post(
      `/agents/${agentId}/actions`,
      validated,
      AgentActionResponseSchema
    )
  },

  /**
   * Update action
   */
  async updateAction(
    agentId: string,
    actionId: string,
    data: UpdateAgentAction
  ): Promise<AgentActionResponse> {
    const validated = UpdateAgentActionSchema.parse(data)

    return put(
      `/agents/${agentId}/actions/${actionId}`,
      validated,
      AgentActionResponseSchema
    )
  },

  /**
   * Delete action
   */
  async deleteAction(agentId: string, actionId: string): Promise<void> {
    await del(`/agents/${agentId}/actions/${actionId}`)
  },
}
